import React, {Fragment, ReactElement, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Pagination,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {SearchBox} from "../../components/SearchBox";
import {Route, Routes} from "react-router-dom";

import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";

import {SortColumn} from "../../api";
import {
    ProductApiGet,
    useDeleteProductByIdMutation,
    useGetProductQuery,
    usePutProductByIdMutation
} from "./product_api";
import placeholder from "../../../assets/imgs/product.jpg";
import Cancel from "@mui/icons-material/Cancel";
import Delete from "@mui/icons-material/Delete";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Upload from "@mui/icons-material/Upload";
import useDownloadImage from "../storage/useDownloadImage";
import ImageUploadDialog from "../storage/ImageUploadDialog";
import usePaginator from "../../hooks/usePaginator";
import {ListCardDataCol, ListGrid} from "../../components/styled";


export type ProductItemProps = {
    item: ProductApiGet
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

const ProductItem = (props: ProductItemProps) => {
    const {item, selectable, onSelect} = props;

    const [uploading, setUploading] = useState(false)


    const [put, {error}] = usePutProductByIdMutation()
    const [deleteProduct, {error: err}] = useDeleteProductByIdMutation()
    const onUpload = (path: string) => {
        put({body: {image_url: path}, id: item.id})
    }
    const imageUrl = useDownloadImage("products", item.image_url)

    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card onClick={() => {
        selectable && onSelect && onSelect(item.id);
    }} sx={{
        display: "flex"

    }}>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={imageUrl || placeholder}
            alt={item.Name + " " + item.Code}
        />
        <ListCardDataCol>
            <CardContent>
                <Typography variant={"subtitle2"} component={"p"}
                            sx={{whiteSpace: "wrap", textOverflow: "ellipsis", wordBreak: "break-all"}}
                            color="secondary">{item.Code} {item.BarCode}</Typography>
                <Typography variant={"h6"}>{item.Name}</Typography>
                <Typography variant={"body1"}>{item.documentID}</Typography>
                <Typography variant={"body1"}>{item.Unittext}</Typography>
            </CardContent>
            {!selectable && <CardActions>
                <Button onClick={() => setUploading(true)}
                        title={"Naloži sliko"}
                ><Upload fontSize={"small"}/></Button>
                <Button
                    title={item.Active ? "Onemogoči" : "Omogoči"}
                    sx={{
                        color: item.Active ? undefined : "error.main"
                    }} onClick={() => {
                    put({body: {Active: !item.Active}, id: item.id})
                }}>{!item.Active ? <Cancel/> : <DoNotDisturb/>}</Button>
                <Button title="Briši produkt"
                        onClick={() => {
                            deleteProduct({id: item.id});
                        }}
                ><Delete fontSize={"small"}/> </Button>
            </CardActions>}
        </ListCardDataCol>

        <ImageUploadDialog
            uploading={uploading}
            onUpload={onUpload}
            bucket={"products"}
            onClose={() => setUploading(false)}
        />
    </Card>
}


export type ProductListProps =
    {
        list: ProductApiGet[],
        selectable?: boolean;
        onSelect?: (id: number) => void;
    }

export const ProductList = (props: ProductListProps) => {
    const {list, onSelect, selectable} = props;


    return <ListGrid>
        {list.map(param => <ProductItem key={param.id}
                                        item={param}
                                        onSelect={onSelect}
                                        selectable={selectable}/>
        )}
    </ListGrid>
}


type INVcControllerProps =
    {
        pageSize?: number;
        selectable?: boolean;
        onSelect?: (id: number) => void;
        customFilter?: ReactElement
    }

function buildSort(sort: string, dir: "asc" | "desc"): SortColumn[] {
    if (sort != "") {
        return [{col: sort, dir}]
    }
    return []
}

const sortFields = {
    Name: "Naziv",
    Code: "Koda",
    BarCode: "Barkoda",
    Unittext: "Enota",
    Active: "Aktiven",
}

export const ProductListController = function (
    {
        pageSize, selectable, onSelect
    }
    : INVcControllerProps) {
    const {
        filter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("products", sortFields)


    const {data, error, isFetching} = useGetProductQuery({
        filter,
        pageSize: pageSize || 25,
        page: page - 1,
        sort: buildSort(sortField, sortDirection)
    });
    useEffect(() => {
        setPage(1)
    }, [filter]);

    if (error) {
        return <SnackError error={error}/>;
    }


    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>
            <SearchBox
                value={filter}
                onChange={setFilter}
            />
            <SortBox
                sortDirection={sortDirection}
                sortField={sortField}
                onChangeDirection={setSortDirection}
                onSelectField={setSortField}
                fields={sortFields}
            />
        </Toolbar>
        {error && <SnackError error={error}/>}
        {(isFetching || !data) && <CircularProgress/>}
        {(!isFetching && data?.result) &&
            <ProductList
                list={data?.result || []}
                selectable={selectable}
                onSelect={onSelect}
            />}
        <Pagination
            page={page}
            onChange={(e, page) => setPage(page)}
            count={Math.ceil((data?.count || 0) / (pageSize || 25))}
        />
    </Fragment>

}
export type INVcProps = {
    pageSize?: number;
}

export default ({pageSize = 25}: INVcProps) => {

    return <Routes>
        <Route path={"/"} element={<Fragment>
            <Title title={"Iskanje artiklov"}/>
            <ProductListController pageSize={pageSize}/>
        </Fragment>}/>

    </Routes>
}
